import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import toast from "../../utils/toast";
import axios from "../../lib/axios";
import { BACKEND_URL } from "../../config";
import { safeGetItem } from "../../utils/storage";

const statusOptions = [
  { label: "All", value: "all" },
  { label: "Active", value: "active" },
  { label: "Pending", value: "pending" },
  { label: "Rejected", value: "rejected" },
  { label: "Blocked", value: "blocked" },
  { label: "Flagged", value: "flagged" },
];

const statusStyles = {
  active: "bg-emerald-500/15 text-emerald-200 border border-emerald-400/30",
  pending: "bg-amber-500/15 text-amber-200 border border-amber-400/30",
  rejected: "bg-rose-500/15 text-rose-200 border border-rose-400/30",
  blocked: "bg-rose-700/20 text-rose-200 border border-rose-500/30",
  flagged: "bg-orange-500/15 text-orange-200 border border-orange-400/30",
};

export default function AdminStudents() {
  const [students, setStudents] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const location = useLocation();

  const token = safeGetItem("token");

  const fetchStudents = async (targetPage = 1, overrideFilter) => {
    const filter = overrideFilter || statusFilter;
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/admin/students`, {
        params: {
          page: targetPage,
          limit: 20,
          status: filter !== "all" ? filter : undefined,
          search: searchTerm || undefined,
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log("Token:", token);
      console.log("API Response:", res.data);
      setStudents(res.data.students || []);
      setPage(res.data.page || targetPage);
      setTotalPages(res.data.totalPages || 1);
      setTotalCount(res.data.totalCount || 0);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Failed to load students");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("filter") === "flagged") {
      setStatusFilter("flagged");
      fetchStudents(1, "flagged");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const studentsToRender = useMemo(() => students, [students]);

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (searchTerm) params.append("search", searchTerm);
      const res = await fetch(`${BACKEND_URL}/admin/students/export?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `students_export_${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      toast.error("Failed to export students");
    }
  };

  const openProfileModal = async (id) => {
    try {
      setViewLoading(true);
      const res = await axios.get(`${BACKEND_URL}/admin/students/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSelectedStudent(res.data);
      setShowProfileModal(true);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load student profile");
    } finally {
      setViewLoading(false);
    }
  };

  const closeProfileModal = () => {
    setShowProfileModal(false);
    setSelectedStudent(null);
  };

  const performAction = async (url, method, successMessage) => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      if (method === "delete") {
        await axios.delete(url, config);
      } else {
        await axios.patch(url, {}, config);
      }
      toast.success(successMessage);
      fetchStudents(page);
      if (showProfileModal) closeProfileModal();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Action failed");
    }
  };

  const approveStudent = (id) =>
    performAction(`${BACKEND_URL}/admin/students/${id}/approve`, "patch", "Student approved");

  const rejectStudent = (id) =>
    performAction(`${BACKEND_URL}/admin/students/${id}/reject`, "patch", "Student rejected");

  const toggleBlock = (id) =>
    performAction(
      `${BACKEND_URL}/admin/students/${id}/toggle-block`,
      "patch",
      "Student status updated"
    );

  const deleteStudent = (id) => {
    if (!window.confirm("Are you sure you want to delete this student?")) return;
    performAction(`${BACKEND_URL}/admin/students/${id}`, "delete", "Student deleted");
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Students</h2>
          <p className="text-white/60 text-sm">
            Monitor, approve, or block students with full context.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name or email"
            className="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-sm text-white placeholder-white/50"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-sm text-white"
          >
            {statusOptions.map((opt) => (
              <option key={opt.value} value={opt.value} className="bg-slate-900">
                {opt.label}
              </option>
            ))}
          </select>
          <button
            onClick={() => fetchStudents(1)}
            className="rounded-2xl border border-white/10 bg-white/10 px-4 py-2 text-sm text-white hover:bg-white/20"
          >
            Apply Filters
          </button>
          <button
            onClick={() => {
              setSearchTerm("");
              setStatusFilter("all");
              fetchStudents(1, "all");
            }}
            className="rounded-2xl border border-white/10 px-4 py-2 text-sm text-white/80 hover:text-white"
          >
            Reset
          </button>
          <button
            onClick={handleExport}
            className="rounded-2xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-2 text-sm text-emerald-200 hover:bg-emerald-500/20"
          >
            Export to CSV
          </button>
        </div>
      </header>

      <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur">
        <div className="grid grid-cols-6 gap-4 px-6 py-3 text-xs uppercase tracking-[3px] text-white/60">
          <span>Name</span>
          <span>Email</span>
          <span>Country</span>
          <span>Status</span>
          <span>Joined</span>
          <span>Actions</span>
        </div>
        <div className="divide-y divide-white/5">
          {loading && <div className="p-6 text-center text-white/70">Loading students...</div>}
          {!loading && studentsToRender.length === 0 && (
            <div className="p-6 text-center text-white/60">No students found.</div>
          )}
          {!loading &&
            studentsToRender.map((student) => {
              const status = (
                student.flagged
                  ? "flagged"
                  : student.isBlocked
                  ? "blocked"
                  : student.status || "active"
              ).toLowerCase();
              return (
                <div
                  key={student._id}
                  className="grid grid-cols-6 gap-4 px-6 py-4 items-center hover:bg-white/5 transition"
                >
                  <div>
                    <p className="font-semibold">{student.name || "Unnamed student"}</p>
                    <p className="text-white/60 text-xs">{student._id}</p>
                  </div>
                  <p className="text-sm">{student.email || "—"}</p>
                  <p className="text-sm">{student.country || "-"}</p>
                  <span
                    className={`px-3 py-1 rounded-full text-xs ${
                      statusStyles[status] || statusStyles.active
                    }`}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </span>
                  <p className="text-sm">
                    {student.createdAt ? new Date(student.createdAt).toLocaleDateString() : "—"}
                  </p>
                  <div className="flex flex-wrap gap-2 text-xs sm:text-sm">
                    <button
                      className="px-3 py-1 rounded-2xl border border-white/10 hover:bg-white/10"
                      onClick={() => openProfileModal(student._id)}
                    >
                      View
                    </button>
                    <button
                      className="px-3 py-1 rounded-2xl border border-emerald-400/30 text-emerald-200 hover:bg-emerald-500/10"
                      onClick={() => approveStudent(student._id)}
                    >
                      Approve
                    </button>
                    <button
                      className="px-3 py-1 rounded-2xl border border-amber-400/30 text-amber-200 hover:bg-amber-500/10"
                      onClick={() => rejectStudent(student._id)}
                    >
                      Reject
                    </button>
                    <button
                      className={`px-3 py-1 rounded-2xl ${
                        status === "blocked"
                          ? "border border-teal-400/30 text-teal-200 hover:bg-teal-500/10"
                          : "border border-rose-400/30 text-rose-200 hover:bg-rose-500/10"
                      }`}
                      onClick={() => toggleBlock(student._id)}
                    >
                      {status === "blocked" ? "Unblock" : "Block"}
                    </button>
                    <button
                      className="px-3 py-1 rounded-2xl border border-white/10 hover:bg-white/10"
                      onClick={() => deleteStudent(student._id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 text-sm text-white/60">
          <span>
            Page {page} of {totalPages} · Total {totalCount} students
          </span>
          <div className="flex items-center gap-2">
            <button
              className="px-3 py-1 rounded-2xl border border-white/10 disabled:opacity-40"
              onClick={() => fetchStudents(page - 1)}
              disabled={page === 1}
            >
              Prev
            </button>
            <button
              className="px-3 py-1 rounded-2xl border border-white/10 disabled:opacity-40"
              onClick={() => fetchStudents(page + 1)}
              disabled={page === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {showProfileModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="relative w-full max-w-3xl rounded-3xl bg-slate-900 border border-white/10 p-6">
            <button
              className="absolute top-4 right-4 text-white/60 hover:text-white"
              onClick={closeProfileModal}
            >
              ✕
            </button>
            {viewLoading || !selectedStudent ? (
              <div className="py-16 text-center text-white/70">Loading profile...</div>
            ) : (
              <div className="space-y-4 text-white">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h3 className="text-2xl font-semibold">{selectedStudent.name || "Unnamed student"}</h3>
                    <p className="text-white/70 text-sm">{selectedStudent.email}</p>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-xs ${
                      statusStyles[
                        (selectedStudent.flagged
                          ? "flagged"
                          : selectedStudent.isBlocked
                          ? "blocked"
                          : selectedStudent.status || "active"
                        ).toLowerCase()
                      ] || statusStyles.active
                    }`}
                  >
                    {(selectedStudent.flagged
                      ? "Flagged"
                      : selectedStudent.isBlocked
                      ? "Blocked"
                      : selectedStudent.status || "Active")}
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-white/60">Country</p>
                    <p>{selectedStudent.country || "-"}</p>
                  </div>
                  <div>
                    <p className="text-white/60">Phone</p>
                    <p>{selectedStudent.profile?.phone || "-"}</p>
                  </div>
                  <div>
                    <p className="text-white/60">City</p>
                    <p>{selectedStudent.profile?.city || "-"}</p>
                  </div>
                  <div>
                    <p className="text-white/60">Education</p>
                    <p>{selectedStudent.profile?.education || "-"}</p>
                  </div>
                  <div>
                    <p className="text-white/60">Preferred Country</p>
                    <p>{selectedStudent.profile?.preferredCountry || "-"}</p>
                  </div>
                  <div>
                    <p className="text-white/60">Preferred Intake</p>
                    <p>{selectedStudent.profile?.preferredIntake || "-"}</p>
                  </div>
                </div>
                {selectedStudent.profile?.notes && (
                  <div>
                    <p className="text-white/60 text-sm mb-1">Notes</p>
                    <p className="text-sm text-white/80">{selectedStudent.profile.notes}</p>
                  </div>
                )}
                <div className="flex flex-wrap gap-3 pt-4">
                  <button
                    className="px-4 py-2 rounded-2xl border border-emerald-400/30 text-emerald-200 hover:bg-emerald-500/10"
                    onClick={() => approveStudent(selectedStudent._id)}
                  >
                    Approve
                  </button>
                  <button
                    className="px-4 py-2 rounded-2xl border border-amber-400/30 text-amber-200 hover:bg-amber-500/10"
                    onClick={() => rejectStudent(selectedStudent._id)}
                  >
                    Reject
                  </button>
                  <button
                    className="px-4 py-2 rounded-2xl border border-rose-400/30 text-rose-200 hover:bg-rose-500/10"
                    onClick={() => toggleBlock(selectedStudent._id)}
                  >
                    {selectedStudent.isBlocked ? "Unblock" : "Block"}
                  </button>
                  <button
                    className="px-4 py-2 rounded-2xl border border-white/10 hover:bg-white/10"
                    onClick={() => deleteStudent(selectedStudent._id)}
                  >
                    Delete
                  </button>
                  <button
                    className="px-4 py-2 rounded-2xl border border-white/10 hover:bg-white/10"
                    onClick={closeProfileModal}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
